'use client'

import { ReactNode } from 'react'
import { ContextualAction, PrimaryAction } from './FourThreeOneNav'

// BIRHAUS: Utility function (replaces @/lib/utils cn)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

// BIRHAUS: Self-contained Button component for actions
function ActionButton({
  children,
  variant = 'default',
  size = 'default',
  disabled = false,
  loading = false,
  onClick,
  className = ''
}: {
  children: ReactNode
  variant?: 'default' | 'secondary' | 'destructive' | 'ghost' | 'primary'
  size?: 'sm' | 'default' | 'lg'
  disabled?: boolean
  loading?: boolean
  onClick?: () => void
  className?: string
}) {
  const baseClasses = "inline-flex items-center justify-center font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
  
  const getVariantClass = (v: typeof variant): string => {
    switch (v) {
      case 'default': return "bg-white border border-gray-300 text-gray-900 hover:bg-gray-50 focus-visible:ring-blue-500"
      case 'secondary': return "bg-gray-100 text-gray-900 hover:bg-gray-200 focus-visible:ring-gray-500"
      case 'destructive': return "bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500"
      case 'ghost': return "text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus-visible:ring-gray-500"
      case 'primary': return "bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500"
      default: return "bg-white border border-gray-300 text-gray-900 hover:bg-gray-50 focus-visible:ring-blue-500"
    }
  }

  const getSizeClass = (s: typeof size): string => {
    switch (s) {
      case 'sm': return "h-8 px-3 text-sm rounded-md"
      case 'default': return "h-10 px-4 py-2 text-sm rounded-md"
      case 'lg': return "h-12 px-6 text-base rounded-lg"
      default: return "h-10 px-4 py-2 text-sm rounded-md"
    }
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={cn(baseClasses, getVariantClass(variant), getSizeClass(size), className)}
    >
      {loading ? (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
      ) : null}
      {children}
    </button>
  )
}

interface ContextualActionsProps {
  contextualActions: ContextualAction[]
  primaryAction?: PrimaryAction
  bp: string
}

export function ContextualActions({
  contextualActions,
  primaryAction,
  bp
}: ContextualActionsProps) {
  return (
    <>
      {/* BIRHAUS: Contextual Actions (max 3) */}
      <div className={cn('hidden', `${bp}:flex`, 'items-center space-x-2')}>
        {contextualActions.map((action) => (
          <ActionButton
            key={action.id}
            onClick={action.onClick}
            variant={action.variant === 'destructive' ? 'destructive' : 'ghost'}
            disabled={action.disabled}
            loading={action.loading}
            size="sm"
          >
            {action.icon && <span className="mr-1">{action.icon}</span>}
            {action.label}
          </ActionButton>
        ))}
      </div>
      
      {/* BIRHAUS: Primary Action */}
      {primaryAction && (
        <ActionButton
          onClick={primaryAction.onClick}
          variant="primary"
          disabled={primaryAction.disabled}
          loading={primaryAction.loading}
          className={cn('hidden', `${bp}:inline-flex`)}
        >
          {primaryAction.icon && <span className="mr-2">{primaryAction.icon}</span>}
          {primaryAction.label}
        </ActionButton>
      )}
    </>
  )
}
